import React, { useState } from 'react'; 
import { useLocalizedStrings } from '../../Localization';
import { Badge, Button, Card, CardBody, CompactSidebarNavTrigger, DataTable, Heading, Icon, NumberDisplay, Page, SidebarNavTrigger } from '@aidkitorg/component-library';
import { AdminPanelNav } from '../AdminPanelApp';
import { ColumnDef } from '@tanstack/react-table';
import { BadgeVariant } from '@aidkitorg/component-library/dist/types/src/Badge';

// FAKE PAYMENT RECIPIENTS DATA
// TODO: move to API or PaymentsService
type PaymentBatchRecipient = {
  uuid: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  proposedDate: Date,
  paymentMethod: '' | 'direct_deposit' | 'ach',
  paymentStatus: '' | 'paused' | 'pending' | 'paid' | 'failed',
  paymentDetails: string,
  treatmentControlGroup: 'treatment' | 'control'
}

const paymentBatchRecipientColumns: ColumnDef<PaymentBatchRecipient>[] = [
  {
    id: 'uuid',
    accessorKey: "uuid",
    enablePinning: true,
    cell: ({ row }) => {
      const uuid = row.original.uuid
      const [copied, setCopied] = useState(false)

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(uuid)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        } catch (err) {
          console.error("Failed to copy: ", err)
        }
      }

      return (
        <div className="flex items-center gap-2">
          <span className="max-w-[160px]">{uuid}</span>
          <Button 
            icon={
              <Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>
            }
            variant="secondary" 
            size="sm"
            aria-label="Copy UUID to clipboard"
            onClick={handleCopy}
          />
        </div>
      )
    }
  },
  {
    accessorKey: "firstName",
    header: "First Name",
  },
  {
    accessorKey: "lastName",
    header:"Last Name",
  },
  {
    accessorKey: "email",
    cell: ({ row }) => (
      <div className="truncate">{row.original.email}</div>
    )
  },
  {
    accessorKey: "phone",
    header: "Phone Number",
  },
  {
    accessorKey: "proposedDate",
    header: "Proposed Date",
    cell: ({ row }) => {
      const date = row.original.proposedDate
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    },
  },
  {
    accessorKey: "paymentMethod",
    header: "Payment Method",
    cell: ({ row }) => {
      const paymentMethod = row.original.paymentMethod

      // TODO: update with all types of payments
      let paymentMethodCopy = ''
      switch(paymentMethod) {
        case 'direct_deposit': 
          paymentMethodCopy = 'Direct Deposit'
          break;
        case 'ach':
          paymentMethodCopy = 'ACH'
          break;
        default:
          paymentMethodCopy = '--'
      }

      return <span>{paymentMethodCopy}</span>
    }
  },
  {
    accessorKey: "paymentStatus",
    header: "Payment Status",
    cell: ({ row }) => {
      const status = row.original.paymentStatus

      if (status === '') {
        return <span>--</span>
      }

      let variant: BadgeVariant;
      let copy = '';

      switch (status) {
        case 'paused':
          variant = 'warning'
          copy = "Paused"
          break;
        case 'pending':
          variant = 'info'
          copy = "Pending"
          break;
        case 'paid':
          variant = 'success'
          copy = "Paid"
          break;
        case 'failed':
          variant = 'error'
          copy = "Failed"
          break;
      }

      return (
        <Badge variant={variant} >{copy}</Badge>
      )
    },
  },
  {
    accessorKey: 'paymentDetails',
    header: "Payment Details",
  },
  {
    accessorKey: "treatmentControlGroup",
    header: "Treatment or Control",
    cell: ({ row }) => {
      const status = row.original.treatmentControlGroup;

      // TODO: update badge with new colors
      return <Badge variant={status === 'treatment' ? 'info' : 'neutral'}>{status === 'treatment' ? 'Treatment' : 'Control'}</Badge>
    }
  },
  {
    id: "view-application",
    header: "View Application",
    cell: ({ row }) => {
      return <Button href='/' variant="link" openInNewWindow>
        <div className="flex gap-2 items-center">
          View Application 
          <Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />
        </div>
      </Button>
    },
    enableSorting: false,
    enablePinning: true,
  }
]

const paymentBatchRecipientData: PaymentBatchRecipient[] = [
  {
    uuid: "1a2b3c4d-0001",
    firstName: "Alice",
    lastName: "Johnson",
    email: "alice.johnson@example.com",
    phone: "+1-555-123-4567",
    paymentMethod: "direct_deposit",
    paymentStatus: "pending",
    proposedDate: new Date("2024-04-01"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0002",
    firstName: "Bob",
    lastName: "Smith",
    email: "bob.smith@example.com",
    phone: "+1-555-987-6543",
    paymentMethod: "ach",
    paymentStatus: "failed",
    proposedDate: new Date("2024-04-03"),
    treatmentControlGroup: "control",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0003",
    firstName: "Carla",
    lastName: "Garcia",
    email: "carla.garcia@example.com",
    phone: "+1-555-222-3333",
    paymentMethod: "",
    paymentStatus: "pending",
    proposedDate: new Date("2024-03-29"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Awaiting bank verification"
  },
  {
    uuid: "1a2b3c4d-0004",
    firstName: "David",
    lastName: "Lee",
    email: "david.lee@example.com",
    phone: "+1-555-333-4444",
    paymentMethod: "ach",
    paymentStatus: "paid",
    proposedDate: new Date("2024-04-05"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0005",
    firstName: "Ella",
    lastName: "Nguyen",
    email: "ella.nguyen@example.com",
    phone: "+1-555-555-1234",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    proposedDate: new Date("2024-03-31"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card mailed"
  },
  {
    uuid: "1a2b3c4d-0006",
    firstName: "Frank",
    lastName: "Williams",
    email: "frank.williams@example.com",
    phone: "+1-555-444-5555",
    paymentMethod: "",
    paymentStatus: "paused",
    proposedDate: new Date("2024-04-06"),
    treatmentControlGroup: "control",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0007",
    firstName: "Grace",
    lastName: "Chen",
    email: "grace.chen@example.com",
    phone: "+1-555-111-2222",
    paymentMethod: "ach",
    paymentStatus: "failed",
    proposedDate: new Date("2024-04-04"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0008",
    firstName: "Henry",
    lastName: "Brown",
    email: "henry.brown@example.com",
    phone: "+1-555-999-8888",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    proposedDate: new Date("2024-04-02"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0009",
    firstName: "Isla",
    lastName: "Martinez",
    email: "isla.martinez@example.com",
    phone: "+1-555-666-7777",
    paymentMethod: "ach",
    paymentStatus: "paused",
    proposedDate: new Date("2024-03-30"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Awaiting bank verification"
  },
  {
    uuid: "1a2b3c4d-0010",
    firstName: "Jack",
    lastName: "Davis",
    email: "jack.davis@example.com",
    phone: "+1-555-000-1111",
    paymentMethod: "",
    paymentStatus: "",
    proposedDate: new Date("2024-04-01"),
    treatmentControlGroup: "control",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0011",
    firstName: "Kara",
    lastName: "Patel",
    email: "kara.patel@example.com",
    phone: "+1-555-321-6543",
    paymentMethod: "ach",
    paymentStatus: "paid",
    proposedDate: new Date("2024-04-06"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card requested"
  },
  {
    uuid: "1a2b3c4d-0012",
    firstName: "Leo",
    lastName: "Wright",
    email: "leo.wright@example.com",
    phone: "+1-555-432-1234",
    paymentMethod: "direct_deposit",
    paymentStatus: "failed",
    proposedDate: new Date("2024-04-07"),
    treatmentControlGroup: "control",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0013",
    firstName: "Maya",
    lastName: "Rivera",
    email: "maya.rivera@example.com",
    phone: "+1-555-777-8888",
    paymentMethod: "",
    paymentStatus: "",
    proposedDate: new Date("2024-03-28"),
    treatmentControlGroup: "treatment",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0014",
    firstName: "Noah",
    lastName: "Kim",
    email: "noah.kim@example.com",
    phone: "+1-555-246-8100",
    paymentMethod: "ach",
    paymentStatus: "paid",
    proposedDate: new Date("2024-04-08"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0015",
    firstName: "Olivia",
    lastName: "Young",
    email: "olivia.young@example.com",
    phone: "+1-555-135-7911",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    proposedDate: new Date("2024-04-09"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card mailed"
  },
]

export const PaymentBatchDetailsPage = () => {
  const L = useLocalizedStrings()

  const paymentBatchRecipientsDataTable = <DataTable 
    columns={paymentBatchRecipientColumns}
    data={paymentBatchRecipientData}
    enableSearch
    searchLabel='Search recipients...'
    columnPinningState={{
      left: ['uuid'],
      right: ['view-application']
    }}
    resizableColumns
  />

  const totalRecipientsCard = <NumberDisplay 
    title="Total Recipients"
    value={paymentBatchRecipientData.length}
    iconName="UsersIcon"
    iconColor='blue'
  />

  const paymentStatusCard = <Card className="min-w-80 rounded-md">
    <CardBody className="py-2 overflow-auto">
      <div className="flex justify-between gap-2">
        <div>
          <div className="text-sm text-gray-500">Payment Status</div>
          <div className="text-2xl font-semibold" tabIndex={0}>Approved</div>
        </div>
        <div className="size-[48px] rounded-md bg-green-100">
          <Icon 
            className="stroke-[1.5] font-semibold m-2 text-green-600"
            size="lg" 
            role="decorative" 
            name="CheckBadgeIcon" />
        </div>

      </div>
    </CardBody>
  </Card>

  const header = (
    <div>
      <div className="flex gap-4 items-center">
        <CompactSidebarNavTrigger />     
        <div className="flex gap-2 items-center">
          <Button href="/admin-panel/payments" variant="link">
            Payments
          </Button>
          <Icon size="sm" role="decorative" name="ChevronRightIcon"/>
          <Button href="/admin-panel/payments/payment-details/payment-id" variant="link">
            Payment Details
          </Button>
        </div>
      </div>
      <div>
        <Heading level="h1" className="m-0">March Housing Assistance</Heading>
      </div>
    </div>
  )
  
  const footer = (
    <div className="w-full flex justify-between">
      <div>
        <Button variant="link" href="/privacy.html" openInNewWindow>
          {L.privacy_policy}
        </Button>
      </div>
      <div>
        <Button variant="link" href="/tos.html" openInNewWindow>
          {L.terms_and_conditions}
        </Button>
      </div>
    </div>
  )
  
  return (
    <Page 
      className="bg-gray-50 h-screen "
      header={header}
      sidebar={
        <AdminPanelNav />
      }
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <div>
            {totalRecipientsCard}
          </div>
          <div>
            {paymentStatusCard}
          </div>
          
        </div>
        <div className="border rounded-md p-4 bg-white">
          {paymentBatchRecipientsDataTable}
        </div>
      </div>
    </Page>
  )
}